<template>
  <v-dialog v-model="dialog" max-width="800" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon :color="task.StatusColor" class="mr-2">{{ getStatusIcon(task.Status) }}</v-icon>
        <span>{{ task.Subject }}</span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" @click="close"></v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text>
        <v-container>
          <!-- Details section moved to top -->
          <v-row v-if="task.Description">
            <v-col cols="12">
              <v-list-item>
                <v-list-item-title class="text-caption text-grey mb-2">Details</v-list-item-title>
                <v-list-item-subtitle>
                  <pre class="text-wrap">{{ getCleanDescription() }}</pre>
                </v-list-item-subtitle>
              </v-list-item>
            </v-col>
          </v-row>
          
          <v-row>
            <v-col cols="12" md="6">
              <v-list-item three-line>
                <v-list-item-title class="text-caption text-grey">Status</v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip :color="task.StatusColor" label small>
                    {{ task.Status }}
                  </v-chip>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="text-caption">
                  {{ task.StatusDescription }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-list-item three-line>
                <v-list-item-title class="text-caption text-grey">Progress</v-list-item-title>
                <v-list-item-subtitle>
                  <v-progress-linear
                    :model-value="task.Progress"
                    :color="task.Progress === 100 ? 'success' : 'primary'"
                    height="25"
                    rounded
                  >
                    <template v-slot:default="{ value }">
                      <strong>{{ Math.ceil(value) }}%</strong>
                    </template>
                  </v-progress-linear>
                </v-list-item-subtitle>
              </v-list-item>
            </v-col>
          </v-row>
          
          <v-row>
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-title class="text-caption text-grey">Start Date</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(task.StartTime) }}</v-list-item-subtitle>
              </v-list-item>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-list-item>
                <v-list-item-title class="text-caption text-grey">End Date</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(task.EndTime) }}</v-list-item-subtitle>
              </v-list-item>
            </v-col>
          </v-row>
          
          <v-row>
            <v-col cols="12">
              <v-list-item>
                <v-list-item-title class="text-caption text-grey">Duration</v-list-item-title>
                <v-list-item-subtitle>{{ getDurationString() }}</v-list-item-subtitle>
              </v-list-item>
            </v-col>
          </v-row>
          
          <!-- Subtasks as v-list -->
          <v-row>
            <v-col cols="12">
              <div class="text-caption text-grey mb-2">Subtasks</div>
              <v-list v-if="subtasks.length > 0" density="compact" class="subtask-list">
                <v-list-item
                  v-for="(subtask, index) in subtasks"
                  :key="subtask.id"
                  class="mb-2"
                >
                  <template v-slot:prepend>
                    <v-icon size="small" :color="subtask.itemStatusColor || 'grey'">
                      {{ subtask.itemId ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}
                    </v-icon>
                  </template>
                  <v-list-item-title>{{ subtask.title }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <span>Progress: {{ subtask.progress || 0 }}%</span>
                    <span v-if="subtask.itemStatusId" class="ml-3">
                      Status: <v-chip size="x-small" :color="subtask.itemStatusColor" label>{{ subtask.itemStatusTitle || subtask.itemStatusId }}</v-chip>
                    </span>
                    <span v-else-if="subtask.itemId" class="ml-3 text-grey">Item assigned</span>
                    <span v-else class="ml-3 text-grey">No item assigned</span>
                  </v-list-item-subtitle>
                  <template v-slot:append v-if="!subtask.itemId">
                    <v-btn
                      icon="mdi-plus"
                      size="small"
                      variant="tonal"
                      color="primary"
                      @click="openLinkSubTask(subtask)"
                      title="Add location"
                    ></v-btn>
                  </template>
                </v-list-item>
              </v-list>
              <div v-else class="text-grey">
                No subtasks found
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  
  <!-- LinkSubTask Dialog -->
  <LinkSubTask 
    v-model:show="showLinkSubTask"
    :selected-subtask="selectedSubtask"
    @subtask-linked="fetchSubtasks"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import LinkSubTask from './LinkSubTask.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  task: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();
const conn = store.getters.getDBConnection;

const subtasks = ref([]);
const showLinkSubTask = ref(false);
const selectedSubtask = ref(null);

const dialog = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  }
});

const close = () => {
  dialog.value = false;
  subtasks.value = [];
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const getDurationString = () => {
  if (!props.task.DurationInHours) return 'N/A';
  const hours = props.task.DurationInHours;
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else {
    const days = Math.round((hours / 24) * 10) / 10;
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
};

const getCleanDescription = () => {
  if (!props.task.Description) return '';
  // Remove the redundant information that's already displayed in other fields
  const lines = props.task.Description.split('\n');
  return lines.filter(line => {
    return !line.startsWith('Status:') && 
           !line.startsWith('Progress:') && 
           !line.startsWith('Duration:') && 
           !line.startsWith('Subtasks:');
  }).join('\n');
};

const getStatusIcon = (status) => {
  const statusIcons = {
    'Overdue': 'mdi-alert-circle',
    'Un-Assigned': 'mdi-account-question',
    'Scheduled': 'mdi-calendar-clock',
    'In Progress': 'mdi-progress-clock',
    'Complete': 'mdi-check-circle'
  };
  return statusIcons[status] || 'mdi-circle';
};

// Fetch subtasks with their item status
const fetchSubtasks = async () => {
  if (!props.task.TaskId) return;
  
  try {
    const result = await conn.query(`
      SELECT 
        s.id,
        s.title,
        s.progress,
        s.itemId,
        s.itemType,
        s.itemTemplateCode,
        s.taskId,
        i.StatusId as itemStatusId,
        st.Title as itemStatusTitle,
        st.Color as itemStatusColor
      FROM subtasks s
      LEFT JOIN items i ON s.itemId = i.Id
      LEFT JOIN status_templates st ON i.StatusId = st.Id
      WHERE s.taskId = '${props.task.ActualTaskId || props.task.TaskId}'
      ORDER BY s.title
    `);
    
    const rows = result.toArray();
    subtasks.value = rows.map(row => row.toJSON());
  } catch (error) {
    console.error('Error fetching subtasks:', error);
    subtasks.value = [];
  }
};

// Open LinkSubTask for subtask
const openLinkSubTask = (subtask) => {
  selectedSubtask.value = subtask;
  showLinkSubTask.value = true;
};



// Watch for dialog changes to fetch subtasks
watch(() => dialog.value, (newValue) => {
  if (newValue && (props.task.ActualTaskId || props.task.TaskId)) {
    fetchSubtasks();
  }
});
</script>

<style scoped>
pre {
  font-family: inherit;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.subtask-list {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  padding: 8px;
}
</style>